import { useTranslation } from '@silospot/i18n/react';
import type { RuleAdoptionDay } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { api } from '../../lib/api';
import { TZ } from '../../lib/datetime';
import { Card, CardHead } from './kit';

/** LOT-1 rules went live on this date — the warm-up axis starts here. */
const RULES_LIVE_SINCE = '2026-07-21';

/** Today's Brussels calendar day as YYYY-MM-DD. */
function todayBrussels(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
}

/** Inclusive list of YYYY-MM-DD days from `start` to `end` (both Brussels days). */
function dayAxis(start: string, end: string): string[] {
  const out: string[] = [];
  const d = new Date(`${start}T12:00:00Z`);
  const last = new Date(`${end}T12:00:00Z`);
  // Guard against a bad clock: cap at 60 rows.
  for (let i = 0; d <= last && i < 60; i++) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

const dayLabel = new Intl.DateTimeFormat('fr-FR', {
  timeZone: TZ,
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
});

/**
 * Discreet warm-up tracker for the LOT-1 recidivism rules: day by day since they
 * went live, how many visits carry each new reason code + the IPv6 share. Days with
 * no traffic show zero (nothing invented). Collapsed by default.
 */
export function RuleAdoptionPanel({ projectId }: { projectId: string }): ReactElement | null {
  const { t } = useTranslation('pages');
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['cld-rule-adoption', projectId],
    queryFn: () => api.getRuleAdoption(projectId),
    enabled: !!projectId && open,
  });

  const byDay = new Map<string, RuleAdoptionDay>((data?.days ?? []).map((d) => [d.day, d]));
  const axis = dayAxis(RULES_LIVE_SINCE, todayBrussels());

  return (
    <Card className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <CardHead title={t('adoption.title')} sub={t('adoption.sub')} />
        <span className="text-[12px] font-semibold text-accent-strong">
          {open ? t('adoption.hide') : t('adoption.show')}
        </span>
      </button>
      {open ? (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.04em] text-text-3">
                {[
                  t('adoption.day'),
                  t('adoption.ruleIp'),
                  t('adoption.ruleRange'),
                  t('adoption.ruleToken'),
                  t('adoption.ipv6'),
                  t('adoption.total'),
                ].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap border-b border-border px-3 py-2 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono">
              {axis.map((day) => {
                const r = byDay.get(day);
                const ipv6Pct = r && r.total > 0 ? Math.round((r.ipv6 / r.total) * 100) : 0;
                return (
                  <tr key={day} className="hover:bg-[#fafbfc]">
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-1.5 font-sans text-text-2">
                      {dayLabel.format(new Date(`${day}T12:00:00Z`))}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-3 py-1.5">
                      {r?.adsIpRecidive ?? 0}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-3 py-1.5">
                      {r?.adsRangeRecidive ?? 0}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-3 py-1.5">{r?.tokenSpread ?? 0}</td>
                    <td className="border-b border-[#f1f3f6] px-3 py-1.5">
                      {r?.ipv6 ?? 0}
                      <span className="ml-1 text-text-3">({ipv6Pct}%)</span>
                    </td>
                    <td className="border-b border-[#f1f3f6] px-3 py-1.5 text-text-3">
                      {r?.total ?? 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}
